import { sortBy } from 'lodash';
import {
  IndemnitesJournalieresPercuesPendantLaPeriodeDArret,
  IndemnitesJournalieresPercuesPendantLaPeriodeDArretRow,
  NewPrejudiceFormPerteGainProfessionnelsActuel,
  NewPrejudiceFormPerteGainProfessionnelsFuturs,
  PerteDeGainsProfessionnelsRow,
  PertesDeGainsProfessionnels,
  PertesDeGainsProfessionnelsPGPF,
  PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte,
  PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte,
  RevenuActiviteAnnuelDeReferenceRow,
  RevenuAnnuelEspere,
  RevenuAnnuelEspereRow,
} from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
import { prejudiceBaseDefaultValues } from '../defaultValues';

// TODO : check if we can replace all this logic by just utility types that allow to define recursive Partial<> types
export type RevenuEspereExistingValues = Omit<Partial<RevenuAnnuelEspere>, 'rows'> & {
  rows?: Partial<RevenuAnnuelEspereRow>[];
};

export type PerteDeGainsProfessionnelsRowExistingValues = Partial<PerteDeGainsProfessionnelsRow>;

export type PerteDeGainsProfessionnelsExistingValues = Omit<Partial<PertesDeGainsProfessionnels>, 'rows'> & {
  rows?: PerteDeGainsProfessionnelsRowExistingValues[];
};

export type IndemnitesJournalieresPercuesPendantLaPeriodeDArretExistingValues = Omit<Partial<IndemnitesJournalieresPercuesPendantLaPeriodeDArret>, 'rows'> & {
  rows?: Partial<IndemnitesJournalieresPercuesPendantLaPeriodeDArretRow>[];
}

export type PGPExistingValues = Omit<
  Partial<PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte>,
  'revenuEspere' |
  'perteDeGainsProfessionnels' |
  'indemnitesJournalieresPercuesPendantLaPeriodeDArret'
> & {
  perteDeGainsProfessionnels?: PerteDeGainsProfessionnelsExistingValues;
  revenuEspere?: RevenuEspereExistingValues;
  indemnitesJournalieresPercuesPendantLaPeriodeDArret?: IndemnitesJournalieresPercuesPendantLaPeriodeDArretExistingValues;
}

export type PerteGainsProfessionnelsPGPFExistingValues = Omit<Partial<PertesDeGainsProfessionnelsPGPF>, 'rows'> & {
  rows?: PerteDeGainsProfessionnelsRowExistingValues[];
};

export type PGPFExistingValues = Omit<
  Partial<PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte>,
  'perteDeGainsProfessionnels' |
  'revenuEspere' |
  'indemnitesJournalieresPercuesPendantLaPeriodeDArret'|
  'capitalisation' |
  'capitalisationTiersPayeurs'
> & {
  perteDeGainsProfessionnels?: PerteGainsProfessionnelsPGPFExistingValues;
  revenuEspere?: RevenuEspereExistingValues;
  indemnitesJournalieresPercuesPendantLaPeriodeDArret?: IndemnitesJournalieresPercuesPendantLaPeriodeDArretExistingValues;
  capitalisation?: Partial<PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte['capitalisation']>;
  capitalisationTiersPayeurs?: Partial<PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte['capitalisationTiersPayeurs']>;
};

export const defaultRevenuEspereRow: RevenuAnnuelEspereRow = {
  revenuEspere: 0,
  unite: '',
  coefficientPerteDeChance: 100,
  perteDeChance: 0,
  numerosPieces: { rows: [] },
};

export const defaultPerteDeChanceDeGainProfessionnelRow: NewPrejudiceFormPerteGainProfessionnelsActuel['perteDeChanceDeGainProfessionnel']['rows'][number] =
  {
    numerosPieces: { rows: [] },
    indemnite: 0,
    unite: '',
    annee: null,
    commentaires: '',
    montantsDejaRevalorises: false,
    indemniteAnnuel: 0,
  };

export const defaultRevenuActiviteAnnuelDeReferenceRow: RevenuActiviteAnnuelDeReferenceRow =
  {
    numerosPieces: { rows: [] },
    annee: 0,
    revenuNet: 0,
    unite: '',
    revenuActiviteAnnuelNet: 0,
    montantsDejaRevalorises: true,
  };

export const defaultPerteDeGainsProfessionnelsRow: PerteDeGainsProfessionnelsRow =
  {
    numerosPieces: { rows: [] },
    dateDebut: null,
    dateFin: null,
    duree: null,
    revenuPercuNet: 0,
    revenuDeReferenceAnnuel: 0,
    revenuDeReferencePeriode: 0,
    revenuPercuParPeriode: 0,
    perteDeGainsProfessionnels: 0,
    unite: '',
    montantsDejaRevalorises: true,
    commentaires: '',
  };

export const defaultIndemnitesJournalieresPercuesPendantLaPeriodeDArretRow: IndemnitesJournalieresPercuesPendantLaPeriodeDArretRow =
  {
    numerosPieces: { rows: [] },
    tiersPayeur: '',
    dateDebut: null,
    dateFin: null,
    duree: null,
    indemniteJournalieresPercuesNet: 0,
    uniteIndemniteJournalieresPercuesNet: '',
    csgRds: null,
    uniteCsgRds: '',
    indemnitesPercuesNetParPeriode: null,
    csgRDSParPeriode: null,
    commentaires: '',
  };

export const pgpaSaisieDirecteDefaultValues = ({
  values,
}: {
  values?: PGPExistingValues;
}): PrejudiceFormPerteGainsProfessionnelsActuelSaisieDirecte => ({
  ...prejudiceBaseDefaultValues({
    values,
  }),
  notes: values?.notes || '',
  revenuEspere: {
    rows:
      values?.revenuEspere?.rows?.map((row) => ({
        ...defaultRevenuEspereRow,
        ...row,
      })) || [],
    total: values?.revenuEspere?.total || 0,
  },
  perteDeGainsProfessionnels: {
    rows:
      sortBy(
        values?.perteDeGainsProfessionnels?.rows,
        (row) => row.dateDebut,
      ).map((row) => ({
        ...defaultPerteDeGainsProfessionnelsRow,
        ...row,
      })) || [],
    total: values?.perteDeGainsProfessionnels?.total || 0,
    revenusPercusTotal:
      values?.perteDeGainsProfessionnels?.revenusPercusTotal || 0,
    revenuReferenceTotal:
      values?.perteDeGainsProfessionnels?.revenuReferenceTotal || 0,
  },
  indemnitesJournalieresPercuesPendantLaPeriodeDArret: {
    rows:
      sortBy(
        values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret?.rows,
        (row) => row.dateDebut,
      ).map((row) => ({
        ...defaultIndemnitesJournalieresPercuesPendantLaPeriodeDArretRow,
        ...row,
      })) || [],
    csgRdsTotal:
      values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret
        ?.csgRdsTotal || null,
    indemnitesJournalieresPercuesNet:
      values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret
        ?.indemnitesJournalieresPercuesNet || null,
  },
  formType: values?.formType || 'SAISIE_DIRECTE',
  revalorisationCoefficientsType:
    values?.revalorisationCoefficientsType || 'annuel',
  total: values?.total || 0,
});

export const newPGPADefaultValues = ({
  values,
}: {
  values?: NewPrejudiceFormPerteGainProfessionnelsActuel;
}): NewPrejudiceFormPerteGainProfessionnelsActuel => ({
  ...prejudiceBaseDefaultValues({
    values,
  }),
  notes: values?.notes || '',
  revenuActiviteAnnuelDeReference: {
    rows:
      sortBy(values?.revenuActiviteAnnuelDeReference?.rows, 'annee').map(
        (row) => ({
          ...defaultRevenuActiviteAnnuelDeReferenceRow,
          ...row,
        }),
      ) || [],
    total: values?.revenuActiviteAnnuelDeReference?.total || 0,
  },
  perteDeGainsProfessionnels: {
    rows:
      sortBy(
        values?.perteDeGainsProfessionnels?.rows,
        (row) => row.dateDebut,
      ).map((row) => ({
        ...defaultPerteDeGainsProfessionnelsRow,
        ...row,
      })) || [],
    total: values?.perteDeGainsProfessionnels?.total || 0,
    revenusPercusTotal:
      values?.perteDeGainsProfessionnels?.revenusPercusTotal || 0,
    revenuReferenceTotal:
      values?.perteDeGainsProfessionnels?.revenuReferenceTotal || 0,
  },
  indemnitesJournalieresPercuesPendantLaPeriodeDArret: {
    rows:
      sortBy(
        values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret?.rows,
        (row) => row.dateDebut,
      ).map((row) => ({
        ...defaultIndemnitesJournalieresPercuesPendantLaPeriodeDArretRow,
        ...row,
      })) || [],
    csgRdsTotal:
      values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret
        ?.csgRdsTotal || null,
    indemnitesJournalieresPercuesNet:
      values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret
        ?.indemnitesJournalieresPercuesNet || null,
  },
  perteDeChanceDeGainProfessionnel: {
    rows:
      values?.perteDeChanceDeGainProfessionnel?.rows.map((row) => ({
        ...row,
      })) || [],
    totalIndemnite:
      values?.perteDeChanceDeGainProfessionnel?.totalIndemnite || 0,
    coefficientPerteDeChance:
      values?.perteDeChanceDeGainProfessionnel?.coefficientPerteDeChance || 100,
    perteDeChance: values?.perteDeChanceDeGainProfessionnel?.perteDeChance || 0,
  },
  total: values?.total || 0,
  formType: values?.formType || 'REVENUS',
  revalorisationCoefficientsType:
    values?.revalorisationCoefficientsType || 'smic',
});

export const pgpfSaisieDirecteDefaultValues = ({
  values,
  procedure,
}: {
  values:
    | PGPFExistingValues
    | undefined;
  procedure: Procedure;
}): PrejudiceFormPerteGainsProfessionnelsFutursSaisieDirecte => ({
  ...pgpaSaisieDirecteDefaultValues({ values }),
  perteDeGainsProfessionnels: {
    ...pgpaSaisieDirecteDefaultValues({ values }).perteDeGainsProfessionnels,
    annualisation: values?.perteDeGainsProfessionnels?.annualisation || 0
  },
  capitalisation: {
    sommeACapitaliser: values?.capitalisation?.sommeACapitaliser || 0,
    coefficient: values?.capitalisation?.coefficient || 0,
    isLastArrerageViager: values?.capitalisation?.isLastArrerageViager || false,
    ageDernierArrerage: values?.capitalisation?.ageDernierArrerage || null,
    bareme:
      (values
        ? values?.capitalisation?.bareme
        : procedure.baremes?.baremeCapitalisationId) ?? '',
    montantCapitalise: values?.capitalisation?.montantCapitalise || 0,
    ageDateAttribution: values?.capitalisation?.ageDateAttribution,
    enableCapitalisationDifferee:
      values?.capitalisation?.enableCapitalisationDifferee ?? false,
  },
  isRentesOption: values?.isRentesOption || false,
  rentes: {
    montant: values?.rentes?.montant || 0,
  },
  capitalisationTiersPayeurs: {
    parTiersPayeur: procedure.tiersPayeurs.map((tiersPayeur) => ({
      tiersPayeur,
      sommeACapitaliser: 0,
      coefficient: 0,
      montantCapitalise: 0,
      ...values?.capitalisationTiersPayeurs?.parTiersPayeur?.find(
        (row) => row.tiersPayeur === tiersPayeur,
      ),
    })),
    montantCapitalise:
      values?.capitalisationTiersPayeurs?.montantCapitalise || 0,
  },
  PGPFReliquat: values?.PGPFReliquat || 0,
  activatePGPFReliquat: values?.activatePGPFReliquat || false,
  totalCapitalisation: values?.totalCapitalisation || 0,
});

export const newPGPFDefaultValues = ({
  procedure,
  values,
}: {
  procedure: Procedure;
  values: NewPrejudiceFormPerteGainProfessionnelsFuturs | undefined;
}): NewPrejudiceFormPerteGainProfessionnelsFuturs => ({
  ...prejudiceBaseDefaultValues({
    values,
  }),
  notes: values?.notes || '',
  revenuActiviteAnnuelDeReference: {
    rows:
      sortBy(values?.revenuActiviteAnnuelDeReference?.rows, 'annee').map(
        (row) => ({
          ...defaultRevenuActiviteAnnuelDeReferenceRow,
          ...row,
        }),
      ) || [],
    total: values?.revenuActiviteAnnuelDeReference?.total || 0,
  },
  perteDeGainsProfessionnels: {
    rows:
      sortBy(
        values?.perteDeGainsProfessionnels?.rows,
        (row) => row.dateDebut,
      ).map((row) => ({
        ...defaultPerteDeGainsProfessionnelsRow,
        ...row,
      })) || [],
    total: values?.perteDeGainsProfessionnels?.total || 0,
    annualisation: values?.perteDeGainsProfessionnels?.annualisation || 0,
    revenusPercusTotal:
      values?.perteDeGainsProfessionnels?.revenusPercusTotal || 0,
    revenuReferenceTotal:
      values?.perteDeGainsProfessionnels?.revenuReferenceTotal || 0,
  },
  indemnitesJournalieresPercuesPendantLaPeriodeDArret: {
    rows:
      sortBy(
        values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret?.rows,
        (row) => row.dateDebut,
      ).map((row) => ({
        ...defaultIndemnitesJournalieresPercuesPendantLaPeriodeDArretRow,
        ...row,
      })) || [],
    csgRdsTotal:
      values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret
        ?.csgRdsTotal || null,
    indemnitesJournalieresPercuesNet:
      values?.indemnitesJournalieresPercuesPendantLaPeriodeDArret
        ?.indemnitesJournalieresPercuesNet || null,
  },
  perteDeChanceDeGainProfessionnel: {
    rows:
      sortBy(
        values?.perteDeChanceDeGainProfessionnel?.rows,
        (row) => row.annee,
      ).map((row) => ({
        ...defaultPerteDeChanceDeGainProfessionnelRow,
        ...row,
      })) || [],
    totalIndemnite:
      values?.perteDeChanceDeGainProfessionnel?.totalIndemnite || 0,
    coefficientPerteDeChance:
      values?.perteDeChanceDeGainProfessionnel?.coefficientPerteDeChance || 100,
    perteDeChance: values?.perteDeChanceDeGainProfessionnel?.perteDeChance || 0,
  },
  typeBareme: values?.typeBareme,
  ageDernierArrerage: values?.ageDernierArrerage || null,
  isLastArrerageViager: values?.isLastArrerageViager || false,
  baremeCapitalisation: values
    ? values?.baremeCapitalisation
    : (procedure.baremes?.baremeCapitalisationId ?? undefined),
  victimeSommeACapitaliser: values?.victimeSommeACapitaliser || 0,
  prixEuroRente: values?.prixEuroRente || 0,
  renteCapitalisee: values?.renteCapitalisee || 0,
  ageDateAttribution: values?.ageDateAttribution,
  capitalisationTiersPayeurs: {
    parTiersPayeur: procedure.tiersPayeurs.map((tiersPayeur) => ({
      tiersPayeur,
      sommeACapitaliser: 0,
      coefficient: 0,
      montantCapitalise: 0,
      ...values?.capitalisationTiersPayeurs?.parTiersPayeur?.find(
        (row) => row.tiersPayeur === tiersPayeur,
      ),
    })),
  },
  tiersPayeursTotalCapitalise: values?.tiersPayeursTotalCapitalise || 0,
  capitalConstitutifRente: values?.capitalConstitutifRente || 0,
  activatePGPFReliquat: values?.activatePGPFReliquat || false,
  PGPFReliquat: values?.PGPFReliquat || 0,
  commentaires: values?.commentaires || '',
  total: values?.total || 0,
  formType: values?.formType || 'REVENUS',
  isRentesOption: values?.isRentesOption || false,
  rentes: values?.rentes || {
    montant: 0,
  },
  revalorisationCoefficientsType:
    values?.revalorisationCoefficientsType || 'smic',
  enableCapitalisationDifferee: values?.enableCapitalisationDifferee ?? false,
});
